import { Country } from '@staycool/location';
import { getServiceUrl, httpGet, httpPost } from '../services/api';
import {
    PaymentProvider,
    ProviderRouting,
    PROVIDERS,
    PROVIDER_TYPE,
    Transaction,
    PnpSettings,
    ExistingPaymentMethod,
    TransactionState,
} from '../services/payments/types';
import { toBase64 } from '../services/util';
import { TransactionsPageTransaction } from '../services/types';

const getUrl = (url) => getServiceUrl('payments2', url);

export async function getTransactionById(id: string) {
    const url = getUrl(`transactions/${id}`);
    return httpGet<Transaction>(url);
}

export async function getFilteredPaymentTransactions(filter) {
    const url = getUrl('transactions/filter');
    return httpPost<Transaction[]>(url, filter);
}

export async function cancelWithdrawalRequest(transactionId: string) {
    const url2 = getUrl('transactions/withdrawal-decision/cancel');
    return httpPost(url2, { transactionId });
}

export async function lockWithdrawalRequest(transactionId: string) {
    const url2 = getUrl('transactions/withdrawal-decision/lock');
    return httpPost(url2, { transactionId });
}

export async function depositMoney(params: DepositParams) {
    const url = getUrl('transactions/deposit');
    return httpPost<any>(url, params);
}

export function depositWithPnp(params: {
    currency: string;
    amount: number;
    country: string;
    bonusCode?: string;
    language: string;
}) {
    const url = getUrl('transactions/deposit/pnp');
    return httpPost<{ url: string }>(url, params);
}

export async function pollDeposit(provider: PROVIDERS, pollStartDate: string) {
    const url = getUrl('transactions/deposit/poll');
    return httpGet<TransactionState>(url, { provider, pollStartDate });
}

export async function requestProviderPayout({
    amount,
    methodId,
    provider,
    providerParams,
}: {
    amount: number;
    methodId?: string;
    provider: string;
    providerParams?: any;
}) {
    const url = getUrl('transactions/withdrawal-request');
    return httpPost<{ url?: string; data?: Record<string, unknown> }>(url, {
        amount,
        methodId,
        provider,
        providerParams,
    });
}

export async function getPaymentProviders(params) {
    const url2 = getUrl('providers');
    return httpGet<{ hasDepositsTurnedOver?: boolean; providers: PaymentProvider[] }>(url2, params);
}

export async function getProviderPaymentMethods(provider: PROVIDERS, providerType: PROVIDER_TYPE) {
    const url = getUrl('accounts');
    return httpGet<ExistingPaymentMethod[]>(url, {
        provider,
        providerType,
    });
}

export async function pollPaymentMethod(queryParams: {
    paymentMethodId: string;
    providerRef: string;
    provider: PROVIDERS;
}) {
    const url = getUrl(`payment-methods/poll`);
    return httpGet<ExistingPaymentMethod>(url, queryParams);
}

export async function removeProviderAccount(provider: PROVIDERS, paymentMethodId: string, verificationState?: string) {
    const url = getUrl('accounts/remove');
    return httpPost(url, { provider, paymentMethodId });
}

export async function getMyCardCount() {
    const url = getUrl('payment-methods/my-card-count');
    return httpGet<{ current: number; limit: number }>(url);
}

export async function providerAction(props: ProviderActionsParams) {
    const url = getUrl('transactions/pre-payment');
    return httpPost<any>(url, props);
}

export async function getProviderMethods(provider, amount, providerType) {
    const url = getUrl('provider/methods');
    return httpGet<any>(url, {
        provider,
        amount,
        providerType,
    });
}

export async function getProviderMethodAmounts(provider, amount, method) {
    const url = getUrl('provider/method/amounts');
    return httpGet(url, { provider, amount, method });
}

export function getProviderRoutings(filters: Partial<Pick<ProviderRouting, 'market' | 'type'>> = {}) {
    const url = getUrl('provider-routings');
    return httpGet<ProviderRouting[]>(url, filters);
}

export async function getUserPaymentsSettings() {
    const url = getUrl('user-settings');
    return httpGet<{ id: number; isThirdPartyConditionsConfirmed: boolean; isDepositLocked: boolean }>(url);
}

export async function saveUserPaymentsSettings(settings) {
    const url2 = getUrl('user-settings');
    return httpPost(url2, settings);
}

export async function getPayAndPlaySettingsByCountry(country: Country) {
    const url = getUrl(`pay-and-play-settings/${country}/public`);
    return httpGet<PnpSettings | undefined>(url);
}

export async function getExchangeRate(provider: PROVIDERS, currency: string) {
    const url = getUrl('exchange-rate');
    return httpGet<string>(url, { provider, currency });
}

export function returnFromPayment(provider: PROVIDERS, payload) {
    const url = getUrl(`transactions/return/${toBase64(provider)}`);
    return httpPost(url, payload);
}

export async function getPaymentsDeposits(filter: {
    created__ge?: string;
    created__lt?: string;
    page_size?: number;
    page: number;
}) {
    const url = getUrl('transactions/deposit/user');
    return httpGet<{ transactions: TransactionsPageTransaction[]; hasNextPage: boolean }>(url, filter);
}

export async function getVerificationCode() {
    const url = getUrl(`verification/code`);
    return httpGet<{ pin: string }>(url);
}

export async function resendVerificationCode(pin: string) {
    const url = getUrl(`verification/code`);
    return httpGet(url, { pin });
}

export async function submitVerificationCode(pin: string, code: string) {
    const url = getUrl(`verification/code`);
    return httpPost<{ pin: string }>(url, { pin, code });
}

type DepositParams = {
    amount: number;
    language: string;
    methodId?: string;
    provider: string;
    providerParams?: any;
};

type ProviderActionsParams = {
    provider: string;
    providerParams: any;
};
